import { useEffect, useState } from 'react';
import {
  backyardTotalMeters,
  clockSeconds,
  formatClock,
  formatDistance,
  gameScoreLabel,
  isSpecialGameScore,
  matchMinute,
  raceStatusLabel,
  showsRaceList,
  showsSides,
  tickerKindIcon,
  type BackyardState,
  type RaceEntry,
  type SportId,
  type TickerClock,
  type TickerEntry,
  type TickerState,
} from '@rabbitfuel/shared';
import { timeShort } from '../lib/format.ts';

/**
 * Anzeige des Live-Tickers.
 *
 * Dieselben Bausteine laufen an drei Stellen: auf der Event-Seite unter der
 * Beschreibung, neben dem Hauptstream und als Einblendung ueber dem Bild.
 * Der Unterschied ist nur `tone` (heller/dunkler Grund) und wie kompakt.
 */

export type TickerTone = 'paper' | 'stage';

/* ------------------------------------------------------------------- Uhr */

/**
 * Laesst die Spieluhr im Browser weiterlaufen.
 *
 * Der Server schickt nur *wann* gestartet wurde -- gezaehlt wird hier. So
 * kostet eine laufende Uhr keine einzige Nachricht pro Sekunde, und nach
 * einem Reconnect stimmt sie sofort wieder.
 */
export function useTickerClock(clock: TickerClock): number {
  const [seconds, setSeconds] = useState(() => clockSeconds(clock));

  useEffect(() => {
    setSeconds(clockSeconds(clock));
    if (!clock.running) return;
    const id = window.setInterval(() => setSeconds(clockSeconds(clock)), 1000);
    return () => window.clearInterval(id);
  }, [clock]);

  return seconds;
}

/**
 * Die laufende Uhr. Ist eine Dauer hinterlegt, steht sie klein dahinter --
 * „47:20 von 1:10:00" sagt mehr als eine nackte Zahl. `bare` laesst sie weg:
 * im Bild steht nur die Zeit.
 */
export function TickerClockLabel({ ticker, bare = false }: { ticker: TickerState; bare?: boolean }) {
  const seconds = useTickerClock(ticker.clock);
  // Fussball zaehlt Minuten, alles andere die volle Uhr.
  const label = ticker.mode === 'match' ? matchMinute(ticker.clock, Date.now()) : formatClock(seconds);
  const target = ticker.clock.targetSeconds;

  return (
    <span className="ticker-clock-wrap">
      <span className={`ticker-clock${ticker.clock.running ? ' is-running' : ''}`}>{label}</span>
      {!bare && target !== null && <span className="ticker-clock__target">von {formatClock(target)}</span>}
    </span>
  );
}

/* ------------------------------------------------------------- Scoreboard */

/** Stand beider Seiten. `bug` ist die kompakte Fassung fuers Overlay. */
export function Scoreboard({
  ticker,
  sport,
  bug = false,
}: {
  ticker: TickerState;
  sport: SportId;
  bug?: boolean;
}) {
  if (!showsSides(ticker.mode, sport)) return null;
  const [home, away] = ticker.sides;
  const tennis = ticker.mode === 'tennis';

  return (
    <div className={`scoreboard${bug ? ' scoreboard--bug' : ''}${tennis ? ' scoreboard--tennis' : ''}`}>
      {/* Ohne Spaltenkoepfe waere „4 · 40 · 1" nicht zu lesen. Im Bug ist
          dafuer kein Platz -- dort tragen die Zahlen sich selbst. */}
      {tennis && !bug && (
        <div className="scoreboard__row scoreboard__legend">
          <span />
          <span />
          <span>Sp</span>
          <span>Pkt</span>
          <span>Sätze</span>
        </div>
      )}
      {[home, away].map((side, i) => (
        <div className="scoreboard__row" key={i}>
          <span className="scoreboard__serve" aria-hidden>
            {tennis && ticker.serving === i ? '●' : ''}
          </span>
          <span className="scoreboard__name" title={side.name}>
            {bug ? side.short || side.name : side.name}
          </span>
          {tennis && <span className="scoreboard__games">{side.games}</span>}
          {tennis && <span className="scoreboard__points">{side.points}</span>}
          <span className="scoreboard__score">{side.score}</span>
        </div>
      ))}
      {tennis && ticker.completedSets.length > 0 && (
        <div className="scoreboard__sets">
          {ticker.completedSets.map(([a, b], i) => (
            <span key={i}>
              {a}:{b}
            </span>
          ))}
          {ticker.tiebreak && <span className="scoreboard__tb">Tiebreak</span>}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- Strecke */

/** Wer ist unterwegs, wer im Ziel. `limit` kuerzt fuer die Einblendung. */
/** Wie lange eine Seite der Liste stehen bleibt, bevor umgeblaettert wird. */
const PAGE_MS = 3000;

/**
 * Blaettert seitenweise durch eine lange Liste.
 *
 * Zahlen und Zeiten stehen dabei still und bleiben ablesbar -- ein weiches
 * Durchscrollen waere ruhiger, aber Platzierungen liest man in Bewegung
 * schlecht. Passt alles auf eine Seite, passiert gar nichts.
 */
export function usePagedList(total: number, rows: number, ms: number = PAGE_MS): number {
  const [start, setStart] = useState(0);

  useEffect(() => {
    setStart(0);
    if (total <= rows) return;
    const id = window.setInterval(
      () => setStart((s) => (s + rows >= total ? 0 : s + rows)),
      ms,
    );
    return () => window.clearInterval(id);
  }, [total, rows, ms]);

  // Nach dem Entfernen von Eintraegen koennte der Anfang ins Leere zeigen.
  return start >= total ? 0 : start;
}

/**
 * Wer ist unterwegs, wer im Ziel. Mit `rows` blaettert die Liste im Takt
 * durch das Feld, statt nur die Spitze und ein „+3 weitere" zu zeigen --
 * so bekommt auch das Mittelfeld seine Sekunden.
 */
export function RaceList({ entries, rows }: { entries: RaceEntry[]; rows?: number }) {
  const size = rows ?? entries.length;
  const start = usePagedList(entries.length, size);
  if (!entries.length) return <p className="ticker-empty">Noch niemand auf der Strecke eingetragen.</p>;

  const shown = rows ? entries.slice(start, start + rows) : entries;
  const pages = rows ? Math.ceil(entries.length / rows) : 1;

  return (
    <div className="racelist-wrap">
      <ul className="racelist">
        {shown.map((e) => (
          <li key={e.id} className={`racelist__row racelist__row--${e.status}`}>
            <span className="bib">{e.bib || '—'}</span>
            <span className="racelist__name">{e.name}</span>
            <span className="racelist__where">
              {/* Beim Ausfall zaehlt der Ausfall, nicht der letzte Messpunkt. */}
              {e.status === 'dnf'
                ? raceStatusLabel.dnf
                : e.status === 'finished' && e.place
                  ? `Platz ${e.place}`
                  : e.checkpoint || raceStatusLabel[e.status]}
            </span>
            <span className="racelist__time">{e.time}</span>
          </li>
        ))}
      </ul>
      {pages > 1 && (
        <div className="racelist__pages" aria-hidden>
          {Array.from({ length: pages }, (_, i) => (
            <span key={i} className={i === Math.floor(start / (rows || 1)) ? 'is-on' : ''} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ Backyard */

/**
 * Die drei Zahlen eines Backyard Ultra: wie weit, wie viele Runden, wie
 * viele noch dabei. Dieselbe Rechnung wie in der Einblendung -- die
 * Gesamtstrecke steht nirgends gespeichert, sie ist Rundenlaenge mal Runden.
 */
export function BackyardBoard({ backyard }: { backyard: BackyardState }) {
  return (
    <div className="backyard">
      <div className="backyard__stat backyard__stat--wide">
        <b>{formatDistance(backyardTotalMeters(backyard))}</b>
        <span>gesamt</span>
      </div>
      <div className="backyard__stat">
        <b>{backyard.loops}</b>
        <span>{backyard.loops === 1 ? 'Loop' : 'Loops'}</span>
      </div>
      <div className="backyard__stat">
        <b>{backyard.runners}</b>
        <span>im Rennen</span>
      </div>
      <p className="backyard__lap">Runde: {formatDistance(backyard.lapMeters)}</p>
    </div>
  );
}

/* ---------------------------------------------------------------- Feed */

export function TickerFeed({
  entries,
  limit,
  onRemove,
}: {
  entries: TickerEntry[];
  limit?: number;
  onRemove?: (id: string) => void;
}) {
  /*
   * Gekuerzt wird nur die Anzeige, nicht der Verlauf: ein Backyard ueber 41
   * Loops hat mehr Meldungen, als hier auf einmal stehen sollen -- wer
   * zurueckblaettern will, holt den Rest mit einem Tipp dazu.
   */
  const [all, setAll] = useState(false);
  const shown = limit && !all ? entries.slice(0, limit) : entries;
  if (!shown.length) return <p className="ticker-empty">Noch keine Meldung im Ticker.</p>;
  const hidden = limit ? entries.length - limit : 0;

  return (
    <>
    <ol className="tickerfeed">
      {shown.map((e) => (
        <li key={e.id}>
          <span className="tickerfeed__mark">{e.mark || timeShort.format(new Date(e.ts))}</span>
          <span className="tickerfeed__icon" aria-hidden>
            {tickerKindIcon(e.kind)}
          </span>
          <span className="tickerfeed__text">{e.text}</span>
          {e.score && <span className="tickerfeed__score">{e.score}</span>}
          {onRemove && (
            <button type="button" className="tickerfeed__del" title="Meldung löschen" onClick={() => onRemove(e.id)}>
              ×
            </button>
          )}
        </li>
      ))}
    </ol>
    {hidden > 0 && (
      <button type="button" className="tickerfeed__more" onClick={() => setAll((v) => !v)}>
        {all ? 'Nur die neuesten anzeigen' : `Ältere Meldungen anzeigen (${hidden})`}
      </button>
    )}
    </>
  );
}

/* ------------------------------------------------ Teilnehmer / Ereignisse */

/** Welche der beiden Listen im Ticker offen ist. */
export type TickerView = 'list' | 'feed';

const VIEW_KEY = 'rabbitfuel:ticker:view';

/**
 * Teilnehmerliste oder letzte Ereignisse -- gemerkt pro Browser. Event-Seite
 * und Stream teilen sich die Wahl: wer sich einmal fuer die Ereignisse
 * entschieden hat, will sie auch im Stream zuerst sehen.
 */
export function useTickerView() {
  const [view, setView] = useState<TickerView>(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === 'feed' ? 'feed' : 'list';
    } catch {
      return 'list';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      /* Private Mode o.ae. -- dann eben ab der Teilnehmerliste. */
    }
  }, [view]);
  return [view, setView] as const;
}

/** Ob es ueberhaupt etwas umzuschalten gibt -- ohne Teilnehmer bleibt nur der Feed. */
export const tickerHasList = (ticker: TickerState): boolean =>
  showsRaceList(ticker.mode) && ticker.entries.length > 0;

/* -------------------------------------------------------- Ganzes Panel */

/**
 * Der komplette Ticker als Block -- Kopf, Stand, dann *eine* Liste:
 * Teilnehmer oder letzte Ereignisse. Untereinander kaeme man bei einem
 * vollen Feld an die Ereignisse nur nach langem Scrollen. Wird auf der
 * Event-Seite und neben dem Hauptstream benutzt.
 *
 * Ohne `view` merkt sich der Ticker die Wahl selbst. Der Stream am Handy
 * reicht sie herein und blendet die eigenen Reiter mit `tabs={false}` aus --
 * dort sitzt die Wahl in den Reitern unter dem Video.
 */
export function LiveTicker({
  ticker,
  feed,
  sport,
  tone = 'paper',
  limit,
  live = false,
  view,
  onView,
  tabs = true,
}: {
  ticker: TickerState;
  feed: TickerEntry[];
  /** Entscheidet mit, ob zwei Zaehler ueberhaupt angezeigt werden. */
  sport: SportId;
  tone?: TickerTone;
  limit?: number;
  live?: boolean;
  view?: TickerView;
  onView?: (view: TickerView) => void;
  tabs?: boolean;
}) {
  const [ownView, setOwnView] = useTickerView();
  if (ticker.mode === 'off') return null;
  const showClock = ticker.mode !== 'tennis' && (ticker.clock.running || ticker.clock.baseSeconds > 0);
  const hasList = tickerHasList(ticker);
  const current: TickerView = hasList ? (view ?? ownView) : 'feed';
  const choose = onView ?? setOwnView;

  return (
    <section className={`ticker ticker--${tone}`}>
      <header className="ticker__head">
        <span className="ticker__label">
          {live && <span className="dot dot--pulse" />}
          Live-Ticker
        </span>
        {ticker.headline && <span className="ticker__headline">{ticker.headline}</span>}
        <span className="ticker__spacer" />
        {/* Der Backyard hat keine Abschnitte -- in der Regie gibt es dafuer
            auch keine Knoepfe, ein Rest von frueher liesse sich nicht wegklicken. */}
        {ticker.period && ticker.mode !== 'backyard' && <span className="ticker__period">{ticker.period}</span>}
        {showClock && <TickerClockLabel ticker={ticker} />}
      </header>

      {showsSides(ticker.mode, sport) && (
        <div className="ticker__board">
          <Scoreboard ticker={ticker} sport={sport} />
          {ticker.mode === 'tennis' && isSpecialGameScore(ticker) && (
            <div className="ticker__game">{gameScoreLabel(ticker)}</div>
          )}
        </div>
      )}

      {ticker.mode === 'backyard' && (
        <div className="ticker__board">
          <BackyardBoard backyard={ticker.backyard} />
        </div>
      )}

      {hasList && tabs && (
        <div className="ticker__tabs" role="tablist" aria-label="Liste im Ticker">
          <button
            type="button"
            role="tab"
            aria-selected={current === 'list'}
            className={current === 'list' ? 'is-active' : undefined}
            onClick={() => choose('list')}
          >
            Teilnehmer
            <span className="ticker__count">{ticker.entries.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={current === 'feed'}
            className={current === 'feed' ? 'is-active' : undefined}
            onClick={() => choose('feed')}
          >
            Letzte Ereignisse
          </button>
        </div>
      )}

      {current === 'list' ? (
        <div className="ticker__board ticker__board--list">
          <RaceList entries={ticker.entries} />
        </div>
      ) : (
        // Als eigener Reiter hat der Feed die volle Hoehe -- ein zweiter
        // Scrollbereich in der Seite waere dann nur im Weg.
        <div className={`ticker__feed${hasList ? ' ticker__feed--tab' : ''}`}>
          <TickerFeed entries={feed} limit={limit} />
        </div>
      )}

      {ticker.note && <p className="ticker__note">{ticker.note}</p>}
    </section>
  );
}
